/**
 * SSE-Client fuer Sidebar-Badges (Welle P3, Joerg 2026-07-06).
 *
 * Basis-Version: verbindet mit /api/portal/sidebar/badges/stream, hoert auf
 * `snapshot`-Events und propagiert die Zaehler an Subscriber. Delta-Push
 * kommt in einem spaeteren Schritt (Anschluss an notifications-Hub).
 *
 * Nutzung:
 *   const badges = useSidebarBadges();
 *   badges.todos   // Zahl
 *   badges.chat    // Zahl
 *   badges.baustelle, badges.freigaben
 */
import { useEffect, useState } from 'react';

export interface BadgeSnapshot {
  todos: number;
  chat: number;
  baustelle: number;
  freigaben: number;
  tagebuch: number;
  /** true wenn der User gerade eine /analyze-Session in onvi-capture laufen hat.
   *  Feature 2026-08-11 (Joerg): pulsierender KI-Chip an der Tagebuch-Sidebar-Zeile. */
  analyzing_tagebuch: boolean;
  /** Joerg 2026-09-11: deutsche Erklaertexte pro Badge fuer den
   *  Mouseover-Tooltip ("5 Abwesenheitsanträge · 4 Korrekturanträge"). */
  tooltips?: Partial<Record<'todos' | 'chat' | 'baustelle' | 'freigaben' | 'tagebuch', string>>;
  /** Joerg 2026-09-11: true nur fuer Nutzer, die die Genehmigungs-Sammelmappe
   *  oeffnen duerfen (Gruppe Geschaeftsfuehrung/Admin) — steuert die
   *  Sichtbarkeit des Sidebar-Menuepunkts. undefined = Snapshot noch nicht da. */
  genehmigungenSichtbar?: boolean;
}

const EMPTY: BadgeSnapshot = {
  todos: 0, chat: 0, baustelle: 0, freigaben: 0, tagebuch: 0, analyzing_tagebuch: false,
};

type Listener = (s: BadgeSnapshot) => void;

class BadgeStream {
  private es: EventSource | null = null;
  private snapshot: BadgeSnapshot = EMPTY;
  private listeners = new Set<Listener>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  connect(): void {
    if (this.es) return;
    try {
      this.es = new EventSource('/api/portal/sidebar/badges/stream', { withCredentials: true });
      this.es.addEventListener('snapshot', (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data) as Partial<BadgeSnapshot>;
          // Snapshot merget nur die Zaehler — analyzing_tagebuch kommt ueber
          // eigenes Event, damit der Puls-Chip nicht auf 60s-Refresh wartet.
          const { analyzing_tagebuch, ...rest } = data;
          void analyzing_tagebuch;
          this.snapshot = { ...this.snapshot, ...rest };
          for (const l of this.listeners) l(this.snapshot);
        } catch {
          // silent
        }
      });
      this.es.addEventListener('analyzing', (ev: MessageEvent) => {
        try {
          const data = JSON.parse(ev.data) as { tagebuch?: boolean };
          this.snapshot = { ...this.snapshot, analyzing_tagebuch: !!data.tagebuch };
          for (const l of this.listeners) l(this.snapshot);
        } catch { /* silent */ }
      });
      this.es.onerror = () => {
        this.es?.close();
        this.es = null;
        if (!this.reconnectTimer) {
          this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
          }, 5000);
        }
      };
    } catch {
      // EventSource not supported — fail silently
    }
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    // Sofort aktueller Wert
    cb(this.snapshot);
    if (this.listeners.size === 1) this.connect();
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size === 0) {
        this.es?.close();
        this.es = null;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      }
    };
  }
}

const stream = new BadgeStream();

export function useSidebarBadges(): BadgeSnapshot {
  const [snapshot, setSnapshot] = useState<BadgeSnapshot>(EMPTY);
  useEffect(() => stream.subscribe(setSnapshot), []);
  return snapshot;
}
